import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronLeft,
  ExternalLink,
  Info,
  Package,
  Pencil,
  PlayCircle,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';

// Direktlink in den Web-Vault (Bitwarden-Hilfe „Link to an item“): der Client
// öffnet den Eintrag über ?itemId=…&action=view. Gilt für Vaultwarden gleich,
// weil derselbe Web-Client ausgeliefert wird. Zeigt nur den Eintrag - das
// Passwort bleibt hinter der entsperrten Sitzung des Nutzers.
const BW_VAULT_ITEM_URL = 'https://bitwarden.itkfm.at/#/vault?action=view&itemId=';

import { z } from 'zod';
import { Card } from '@/components/ui/Card';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Tabs } from '@/components/ui/Tabs';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { RelativeTime } from '@/components/ui/RelativeTime';
import { RunStatusBadge, runStatusLabel } from '@/components/ui/RunStatusBadge';
import { systemHasDistinctHostname, systemPrimaryName } from '@/components/ui/SystemName';
import { AuthModeBadge, keyBelegQuelle } from '@/components/systems/AuthModeBadge';
import { SystemFormDialog } from '@/components/systems/SystemFormDialog';
import type { SystemFormDialogState } from '@/components/systems/SystemFormDialog';
import {
  useDeleteSystem,
  useSystem,
  useSystemServices,
  type ServiceDriftStatus,
  type ServiceWithDrift,
  type SystemDetail,
} from '@/hooks/useSystems';
import {
  useConnectionProfiles,
  useCreateConnectionProfile,
  useDeleteConnectionProfile,
  useUpdateConnectionProfile,
} from '@/hooks/useConnectionProfiles';
import { useCustomer } from '@/hooks/useCustomers';
import { useRuns, type Run } from '@/hooks/useRuns';
import { useZabbixPackages } from '@/hooks/useZabbixPackages';
import { ApiError } from '@/lib/api';
import { formatDateTime, formatNumber, policyLabel, scopeLabel } from '@/lib/format';
import type {
  ConnectionProfile,
  ConnectionProfileCreate,
  ConnectionProtocol,
  SystemScope,
  ZabbixPackageItem,
  ZabbixPackagesResponse,
} from '@/types/api';

/**
 * System-Detail (Sprint 1, WT-103).
 *
 * Sub-Tabs:
 *  - overview     ✅ alle DB-Felder (OS, IP, Notes, Wartungsdatum, ...)
 *  - connections  ✅ Connection-Profile (ssh/rdp/https) mit CRUD + aktiv/inaktiv
 *  - services     ✅ Stacks pro System mit observed_version
 *  - history      ✅ Wartungs-History (gefiltert aus Runs über target_system_ids)
 *  - packages     ✅ installierte Pakete live aus Zabbix (On-Demand, S8-03)
 *                    inkl. APT-Update-Sicht (itkfm.apt.*-Items, S8-06)
 */

const TABS = ['overview', 'connections', 'services', 'history', 'packages'] as const;
type SubTab = (typeof TABS)[number];

const searchSchema = z.object({
  tab: z.enum(TABS).optional(),
});

export const Route = createFileRoute('/systems/$id')({
  component: SystemDetailPage,
  validateSearch: (s) => searchSchema.parse(s),
});

function SystemDetailPage(): JSX.Element {
  const { id } = Route.useParams();
  const { tab = 'overview' } = Route.useSearch();
  const navigate = useNavigate({ from: '/systems/$id' });

  const systemQuery = useSystem(id);
  const customerId = systemQuery.data?.customer_id;
  const customerQuery = useCustomer(customerId);
  const deleteMutation = useDeleteSystem();

  const [formState, setFormState] = useState<SystemFormDialogState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const setTab = (next: SubTab): void => {
    void navigate({ search: { tab: next === 'overview' ? undefined : next } });
  };

  const runDelete = (): void => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        setConfirmDelete(false);
        void navigate({ to: '/systems' });
      },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <Link
        to="/systems"
        className="inline-flex items-center gap-1 text-sm text-muted hover:text-text"
      >
        <ChevronLeft className="size-4" />
        Zurück zur Liste
      </Link>

      {systemQuery.isLoading && <SkeletonRows rows={3} cols={3} />}
      {systemQuery.isError && <ErrorAlert error={systemQuery.error} />}

      {systemQuery.data && (
        <>
          <header className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold">
                {systemPrimaryName(systemQuery.data)}
              </h1>
              <p className="flex flex-wrap items-center gap-2 text-sm text-muted">
                {systemHasDistinctHostname(systemQuery.data) && (
                  <>
                    <span className="font-mono">{systemQuery.data.hostname}</span>
                    {customerQuery.data && <span aria-hidden="true">•</span>}
                  </>
                )}
                {customerQuery.data && (
                  <Link
                    to="/customers/$id"
                    params={{ id: customerQuery.data.id }}
                    className="text-primary hover:underline"
                  >
                    {customerQuery.data.name}
                  </Link>
                )}
                <Badge tone={scopeTone(systemQuery.data.scope)}>
                  {scopeLabel(systemQuery.data.scope)}
                </Badge>
                {systemQuery.data.deactivated_at && (
                  <Badge tone="warning">deaktiviert</Badge>
                )}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                leftIcon={<Pencil className="size-4" />}
                onClick={() => {
                  if (systemQuery.data) {
                    setFormState({ mode: 'edit', system: systemQuery.data });
                  }
                }}
                data-testid="system-edit"
              >
                Bearbeiten
              </Button>
              <Button
                variant="ghost"
                className="text-danger"
                leftIcon={<Trash2 className="size-4" />}
                onClick={() => {
                  deleteMutation.reset();
                  setConfirmDelete(true);
                }}
                disabled={Boolean(systemQuery.data.deactivated_at)}
              >
                Löschen
              </Button>
            </div>
          </header>

          <SystemFormDialog state={formState} onClose={() => setFormState(null)} />

          <Dialog
            open={confirmDelete}
            onClose={() => setConfirmDelete(false)}
            title="System löschen?"
            description={systemPrimaryName(systemQuery.data)}
            footer={
              <>
                <Button
                  variant="ghost"
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleteMutation.isPending}
                >
                  Abbrechen
                </Button>
                <Button
                  variant="danger"
                  onClick={runDelete}
                  loading={deleteMutation.isPending}
                  data-testid="system-confirm-delete"
                >
                  Löschen
                </Button>
              </>
            }
          >
            <div className="flex flex-col gap-2 text-sm">
              <p>
                Soft-Delete: das System wird deaktiviert und aus Listen ausgeblendet;
                Run-History bleibt erhalten.
              </p>
              {deleteMutation.isError && <ErrorAlert error={deleteMutation.error} />}
            </div>
          </Dialog>

          <Tabs<SubTab>
            ariaLabel="Detail-Bereiche"
            active={tab}
            onChange={setTab}
            items={[
              { id: 'overview', label: 'Übersicht' },
              { id: 'connections', label: 'Connection-Profiles' },
              { id: 'services', label: 'Services' },
              { id: 'history', label: 'Wartungs-History' },
              { id: 'packages', label: 'Pakete' },
            ]}
          >
            {tab === 'overview' && <OverviewTab system={systemQuery.data} />}
            {tab === 'connections' && <ConnectionsTab systemId={id} />}
            {tab === 'services' && <ServicesTab systemId={id} />}
            {tab === 'history' && (
              <HistoryTab systemId={id} customerId={systemQuery.data.customer_id} />
            )}
            {tab === 'packages' && <PackagesTab systemId={id} active={tab === 'packages'} />}
          </Tabs>
        </>
      )}
    </div>
  );
}

function OverviewTab({ system }: { system: SystemDetail }): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Card title="Stammdaten">
        <dl className="grid grid-cols-3 gap-2 text-sm">
          <dt className="text-muted">Hostname</dt>
          <dd className="col-span-2 font-mono">{system.hostname}</dd>
          <dt className="text-muted">Display-Name</dt>
          <dd className="col-span-2">{system.display_name ?? '–'}</dd>
          <dt className="text-muted">FQDN</dt>
          <dd className="col-span-2 font-mono">{system.fqdn ?? '–'}</dd>
          <dt className="text-muted">Rolle</dt>
          <dd className="col-span-2">
            <Badge tone="neutral">{system.role ?? 'unbekannt'}</Badge>
          </dd>
          <dt className="text-muted">OS</dt>
          <dd className="col-span-2">
            {system.os_family ?? '–'}
            {system.os_version && (
              <span className="ml-1 text-muted">{system.os_version}</span>
            )}
          </dd>
          <dt className="text-muted">Primäre IP</dt>
          <dd className="col-span-2 font-mono">{system.primary_ip ?? '–'}</dd>
          <dt className="text-muted">IP-Adressen</dt>
          <dd className="col-span-2 font-mono">
            {system.ip_addresses && system.ip_addresses.length > 0
              ? system.ip_addresses.join(', ')
              : system.secondary_ips && system.secondary_ips.length > 0
                ? system.secondary_ips.join(', ')
                : '–'}
          </dd>
        </dl>
      </Card>

      <Card title="Wartungs-Konfiguration">
        <dl className="grid grid-cols-3 gap-2 text-sm">
          <dt className="text-muted">Update-Policy</dt>
          <dd className="col-span-2">
            <Badge tone={updatePolicyTone(system.update_policy)}>
              {policyLabel(system.update_policy)}
            </Badge>
          </dd>
          <dt className="text-muted">Backup</dt>
          <dd className="col-span-2">
            {system.backup_enabled ? (
              <Badge tone="success">aktiv</Badge>
            ) : (
              <Badge tone="neutral">nein</Badge>
            )}
          </dd>
          <dt className="text-muted">Koord. mit Kunde</dt>
          <dd className="col-span-2">
            {system.coordinated_with_customer ? (
              <Badge tone="primary">ja</Badge>
            ) : (
              <Badge tone="neutral">nein</Badge>
            )}
          </dd>
          <dt className="text-muted">Scope</dt>
          <dd className="col-span-2">
            <Badge tone={scopeTone(system.scope)}>{scopeLabel(system.scope)}</Badge>
          </dd>
          <dt className="text-muted">Kritisch</dt>
          <dd className="col-span-2">
            {system.is_critical ? (
              <Badge tone="danger">ja</Badge>
            ) : (
              <Badge tone="neutral">nein</Badge>
            )}
          </dd>
          <dt className="text-muted">Letzte Wartung</dt>
          <dd className="col-span-2">{formatDateTime(system.last_maintained_at)}</dd>
          <dt className="text-muted">Erstellt</dt>
          <dd className="col-span-2 text-muted">{formatDateTime(system.created_at)}</dd>
          <dt className="text-muted">Aktualisiert</dt>
          <dd className="col-span-2 text-muted">{formatDateTime(system.updated_at)}</dd>
        </dl>
      </Card>

      <Card
        title="Inventar & Zugang"
        description="Metadaten für Import/Export und den Client — Secrets bleiben am Sysadmin-PC."
        className="lg:col-span-2"
      >
        <dl className="grid grid-cols-3 gap-2 text-sm lg:grid-cols-6">
          <dt className="text-muted">Ansible-Alias</dt>
          <dd className="col-span-2 font-mono">{system.ansible_alias ?? '–'}</dd>
          <dt className="text-muted">Verwaltet</dt>
          <dd className="col-span-2">
            {system.managed ? (
              <Badge tone="success">ja</Badge>
            ) : (
              <Badge tone="neutral">nein</Badge>
            )}
          </dd>
          <dt className="text-muted">Zugangsdaten lokal</dt>
          <dd className="col-span-2">
            {system.credentials_available ? (
              <Badge tone="success">vorhanden</Badge>
            ) : (
              <Badge tone="neutral">nein</Badge>
            )}
          </dd>
          <dt className="text-muted">Bitwarden</dt>
          <dd className="col-span-2">
            {/* Nur die Item-Referenz (Regel-2-Lockerung 12.08.2026) — das
                Passwort löst ausschließlich der Client lokal auf. */}
            {system.bw_item_id ? (
              <span className="inline-flex flex-wrap items-center gap-2">
                <Badge tone="success" data-testid="system-bw-linked">
                  Bitwarden verknüpft
                </Badge>
                <span className="font-mono text-xs text-muted" title={system.bw_item_id}>
                  {shortBwItemId(system.bw_item_id)}
                </span>
                <a
                  href={`${BW_VAULT_ITEM_URL}${encodeURIComponent(system.bw_item_id)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  data-testid="system-bw-vault-link"
                >
                  <ExternalLink className="size-3" aria-hidden="true" />
                  Im Tresor öffnen
                </a>
              </span>
            ) : (
              <Badge tone="neutral">kein Bitwarden-Item</Badge>
            )}
          </dd>
          {/* Anmeldewege (Migration 0040): der Yubikey-Schlüssel kommt ZUM
              Tresor-Passwort dazu, er ersetzt es nicht. Die Marke steht
              deshalb direkt unter der Bitwarden-Zeile - beide Wege gehören
              nebeneinander gelesen. */}
          <dt className="text-muted">Anmeldewege</dt>
          <dd className="col-span-2">
            <span className="inline-flex flex-wrap items-center gap-2">
              <AuthModeBadge
                authMode={system.auth_mode}
                data-testid="system-auth-mode"
              />
              {system.key_verified_at ? (
                <span
                  className="text-xs text-muted"
                  title={`Beleg: ${keyBelegQuelle(system.key_verified_by)}. Beobachtung mit Zeitstempel, kein Live-Test.`}
                  data-testid="system-key-verified"
                >
                  Key belegt <RelativeTime value={system.key_verified_at} /> (
                  {keyBelegQuelle(system.key_verified_by)})
                </span>
              ) : (
                <span className="text-xs text-muted" data-testid="system-key-verified">
                  Key noch nie belegt
                </span>
              )}
              <Link
                to="/systems/zugangsmigration"
                className="text-xs text-primary underline-offset-2 hover:underline"
              >
                Zugangsmigration
              </Link>
            </span>
          </dd>
          <dt className="text-muted">VPN-Profil</dt>
          <dd className="col-span-2 font-mono">{system.vpn_ref ?? '–'}</dd>
          <dt className="text-muted">Proxmox</dt>
          <dd className="col-span-2 font-mono">
            {system.proxmox_node ?? '–'}
            {system.proxmox_vmid != null && ` / ${system.proxmox_vmid}`}
          </dd>
          <dt className="text-muted">Quelle</dt>
          <dd className="col-span-2 truncate font-mono" title={system.source_ref ?? undefined}>
            {system.source_ref ?? '–'}
          </dd>
        </dl>
      </Card>

      <Card title="Notizen" className="lg:col-span-2">
        {system.notes ? (
          <pre className="whitespace-pre-wrap font-sans text-sm">{system.notes}</pre>
        ) : (
          <p className="text-sm text-muted">Keine Notizen.</p>
        )}
      </Card>
    </div>
  );
}

// --- Connection-Profile (CRUD) -------------------------------------------

const PROTOCOL_OPTIONS: readonly ConnectionProtocol[] = ['ssh', 'rdp', 'https'];

const PROTOCOL_DEFAULT_PORTS: Record<ConnectionProtocol, number> = {
  ssh: 22,
  rdp: 3389,
  https: 443,
};

function isKnownProtocol(value: string): value is ConnectionProtocol {
  return (PROTOCOL_OPTIONS as readonly string[]).includes(value);
}

interface ProfileFormState {
  protocol: ConnectionProtocol;
  port: string;
  username: string;
  notes: string;
}

function emptyProfileForm(): ProfileFormState {
  return {
    protocol: 'ssh',
    port: String(PROTOCOL_DEFAULT_PORTS.ssh),
    username: '',
    notes: '',
  };
}

// Unbekannte Protokolle aus Import-Altbestand (vnc, winrm, ...) fallen beim
// Bearbeiten auf 'ssh' zurück — der Select bietet nur ssh/rdp/https an.
function formFromProfile(profile: ConnectionProfile): ProfileFormState {
  return {
    protocol: isKnownProtocol(profile.protocol) ? profile.protocol : 'ssh',
    port: profile.port != null ? String(profile.port) : '',
    username: profile.username ?? '',
    notes: profile.notes ?? '',
  };
}

type ProfileDialogState = { mode: 'create' } | { mode: 'edit'; profile: ConnectionProfile };
type ProfileConfirmState = { kind: 'delete' | 'deactivate'; profile: ConnectionProfile };

function ConnectionsTab({ systemId }: { systemId: string }): JSX.Element {
  const { data, isLoading, isError, error } = useConnectionProfiles(systemId);
  const createMutation = useCreateConnectionProfile();
  const updateMutation = useUpdateConnectionProfile();
  const deleteMutation = useDeleteConnectionProfile();

  const [dialog, setDialog] = useState<ProfileDialogState | null>(null);
  const [confirmAction, setConfirmAction] = useState<ProfileConfirmState | null>(null);
  const [showSshConfigHint, setShowSshConfigHint] = useState(false);

  const openDialog = useCallback((next: ProfileDialogState): void => {
    createMutation.reset();
    updateMutation.reset();
    setDialog(next);
  }, [createMutation, updateMutation]);

  const openConfirm = useCallback((next: ProfileConfirmState): void => {
    updateMutation.reset();
    deleteMutation.reset();
    setConfirmAction(next);
  }, [updateMutation, deleteMutation]);

  const submitForm = (form: ProfileFormState): void => {
    const body: ConnectionProfileCreate = {
      protocol: form.protocol,
      port: form.port.trim() === '' ? null : Number(form.port.trim()),
      username: form.username.trim() === '' ? null : form.username.trim(),
      notes: form.notes.trim() === '' ? null : form.notes.trim(),
    };
    const onSuccess = (): void => {
      setShowSshConfigHint(true);
      setDialog(null);
    };
    if (dialog?.mode === 'edit') {
      updateMutation.mutate({ profileId: dialog.profile.id, body }, { onSuccess });
    } else {
      createMutation.mutate({ systemId, body }, { onSuccess });
    }
  };

  const toggleActive = useCallback((profile: ConnectionProfile): void => {
    if (profile.is_active) {
      openConfirm({ kind: 'deactivate', profile });
      return;
    }
    updateMutation.mutate(
      { profileId: profile.id, body: { is_active: true } },
      { onSuccess: () => setShowSshConfigHint(true) }
    );
  }, [openConfirm, updateMutation]);

  const runConfirmed = (): void => {
    if (!confirmAction) return;
    const onSuccess = (): void => {
      setShowSshConfigHint(true);
      setConfirmAction(null);
    };
    if (confirmAction.kind === 'delete') {
      deleteMutation.mutate({ profileId: confirmAction.profile.id }, { onSuccess });
    } else {
      updateMutation.mutate(
        { profileId: confirmAction.profile.id, body: { is_active: false } },
        { onSuccess }
      );
    }
  };

  const profiles = data ?? [];

  const columns = useMemo<ReadonlyArray<Column<ConnectionProfile>>>(
    () => [
      {
        id: 'protokoll',
        header: 'Protokoll',
        filter: 'select',
        filterValue: (profile) => profile.protocol,
        cell: (profile) => (
          <Badge tone={protocolTone(profile.protocol)}>{profile.protocol}</Badge>
        ),
      },
      {
        id: 'user-port',
        header: 'User@Port',
        className: 'font-mono text-xs',
        cell: (profile) => userAtPort(profile),
      },
      {
        id: 'status',
        header: 'Status',
        filter: 'select',
        filterValue: (profile) => (profile.is_active ? 'aktiv' : 'inaktiv'),
        cell: (profile) =>
          profile.is_active ? (
            <Badge tone="success">aktiv</Badge>
          ) : (
            <Badge tone="neutral">inaktiv</Badge>
          ),
      },
      {
        id: 'notes',
        header: 'Notes',
        className: 'max-w-xs',
        filterValue: (profile) => profile.notes ?? '',
        cell: (profile) =>
          profile.notes ? (
            <span className="line-clamp-2 text-xs text-muted">{profile.notes}</span>
          ) : (
            <span className="text-muted">–</span>
          ),
      },
      {
        id: 'angelegt',
        header: 'Angelegt',
        className: 'text-muted',
        filterValue: (profile) => (profile.created_at ? formatDateTime(profile.created_at) : ''),
        cell: (profile) => formatDateTime(profile.created_at),
      },
      {
        id: 'aktionen',
        header: 'Aktionen',
        headerClassName: 'text-right',
        className: 'text-right',
        filter: false,
        cell: (profile) => (
          <span className="inline-flex flex-wrap justify-end gap-1">
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<Pencil className="size-3" />}
              onClick={() => openDialog({ mode: 'edit', profile })}
            >
              Bearbeiten
            </Button>
            <Button size="sm" variant="ghost" onClick={() => toggleActive(profile)}>
              {profile.is_active ? 'Deaktivieren' : 'Aktivieren'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-danger"
              leftIcon={<Trash2 className="size-3" />}
              onClick={() => openConfirm({ kind: 'delete', profile })}
            >
              Löschen
            </Button>
          </span>
        ),
      },
    ],
    // openDialog / toggleActive / openConfirm kommen aus dem Render;
    // useMemo hält die Spalten trotzdem zusammen, solange die Handler stabil bleiben.
    [openDialog, toggleActive, openConfirm]
  );

  if (isLoading) return <SkeletonRows rows={3} cols={4} />;
  if (isError) return <ErrorAlert error={error} />;

  return (
    <div className="flex flex-col gap-3">
      {showSshConfigHint && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-md border border-primary/40 bg-primary/10 p-3 text-sm"
          data-testid="ssh-config-hint"
        >
          <Info className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
          <span>
            ssh_config neu generieren (
            <Link to="/settings/ssh-config" className="text-primary underline">
              Settings → SSH-Config
            </Link>
            ), damit Sessions das Profil kennen.
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm text-muted">
          {profiles.length} {profiles.length === 1 ? 'Profil' : 'Profile'}
        </span>
        <Button
          size="sm"
          leftIcon={<Plus className="size-4" />}
          onClick={() => openDialog({ mode: 'create' })}
          data-testid="profile-create"
        >
          Profil anlegen
        </Button>
      </div>

      {profiles.length === 0 ? (
        <EmptyState
          title="Keine Connection-Profile"
          description="Ohne aktives SSH-Profil kann die Run-Engine dieses System nicht warten. Jetzt eines anlegen."
          actions={
            <Button
              leftIcon={<Plus className="size-4" />}
              onClick={() => openDialog({ mode: 'create' })}
            >
              Profil anlegen
            </Button>
          }
        />
      ) : (
        <DataTable
          caption="Connection-Profile dieses Systems"
          columns={columns}
          rows={profiles}
          rowKey={(profile) => profile.id}
          storageKey="system-connection-profiles"
          rowMuted={(profile) => !profile.is_active}
          rowTestId={(profile) => `profile-row-${profile.id}`}
        />
      )}

      <ProfileFormDialog
        state={dialog}
        onClose={() => setDialog(null)}
        onSubmit={submitForm}
        saving={createMutation.isPending || updateMutation.isPending}
        error={dialog?.mode === 'edit' ? updateMutation.error : createMutation.error}
      />

      <ProfileConfirmDialog
        action={confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={runConfirmed}
        busy={deleteMutation.isPending || updateMutation.isPending}
        error={confirmAction?.kind === 'delete' ? deleteMutation.error : updateMutation.error}
      />
    </div>
  );
}

interface ProfileFormDialogProps {
  state: ProfileDialogState | null;
  onClose: () => void;
  onSubmit: (form: ProfileFormState) => void;
  saving: boolean;
  error: Error | null;
}

function ProfileFormDialog({
  state,
  onClose,
  onSubmit,
  saving,
  error,
}: ProfileFormDialogProps): JSX.Element {
  const [form, setForm] = useState<ProfileFormState>(emptyProfileForm());

  // Formular beim Öffnen mit Create-Defaults bzw. Edit-Werten befüllen.
  useEffect(() => {
    if (!state) return;
    setForm(state.mode === 'edit' ? formFromProfile(state.profile) : emptyProfileForm());
  }, [state]);

  // Port folgt dem Protokoll-Default (22/3389/443), solange der User ihn
  // nicht selbst angefasst hat (= Feld leer oder noch auf altem Default).
  const changeProtocol = (next: ConnectionProtocol): void => {
    setForm((f) => {
      const prevDefault = String(PROTOCOL_DEFAULT_PORTS[f.protocol]);
      const port =
        f.port.trim() === '' || f.port === prevDefault
          ? String(PROTOCOL_DEFAULT_PORTS[next])
          : f.port;
      return { ...f, protocol: next, port };
    });
  };

  const portTrimmed = form.port.trim();
  const portInvalid =
    portTrimmed !== '' &&
    (!/^\d+$/.test(portTrimmed) || Number(portTrimmed) < 1 || Number(portTrimmed) > 65535);

  return (
    <Dialog
      open={Boolean(state)}
      onClose={onClose}
      title={state?.mode === 'edit' ? 'Profil bearbeiten' : 'Profil anlegen'}
      description={
        state?.mode === 'edit'
          ? 'Änderungen gelten für kommende Runs und Sessions.'
          : 'Zugangs-Profil für dieses System (Run-Engine nutzt aktive SSH-Profile).'
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            onClick={() => onSubmit(form)}
            loading={saving}
            disabled={portInvalid}
            data-testid="profile-save"
          >
            Speichern
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Protokoll</span>
          <select
            value={form.protocol}
            onChange={(e) => changeProtocol(e.target.value as ConnectionProtocol)}
            className="rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            data-testid="profile-protocol"
          >
            {PROTOCOL_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <Input
          label="Port"
          inputMode="numeric"
          value={form.port}
          onChange={(e) => setForm((f) => ({ ...f, port: e.target.value }))}
          error={portInvalid ? 'Port muss zwischen 1 und 65535 liegen.' : undefined}
          hint={`Default für ${form.protocol}: ${PROTOCOL_DEFAULT_PORTS[form.protocol]}`}
        />
        <Input
          label="Username"
          value={form.username}
          onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
          placeholder="root"
        />
        <Input
          label="Notes"
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          hint="Optional, z.B. Jump-Host oder Besonderheiten."
        />
        {error && <ErrorAlert error={error} />}
      </div>
    </Dialog>
  );
}

interface ProfileConfirmDialogProps {
  action: ProfileConfirmState | null;
  onClose: () => void;
  onConfirm: () => void;
  busy: boolean;
  error: Error | null;
}

function ProfileConfirmDialog({
  action,
  onClose,
  onConfirm,
  busy,
  error,
}: ProfileConfirmDialogProps): JSX.Element {
  const isDelete = action?.kind === 'delete';
  return (
    <Dialog
      open={Boolean(action)}
      onClose={onClose}
      title={isDelete ? 'Profil löschen?' : 'Profil deaktivieren?'}
      {...(action
        ? { description: `${action.profile.protocol}-Profil ${userAtPort(action.profile)}` }
        : {})}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            loading={busy}
            data-testid="profile-confirm"
          >
            {isDelete ? 'Löschen' : 'Deaktivieren'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-2 text-sm">
        <p>
          {isDelete
            ? 'Das Profil wird endgültig entfernt.'
            : 'Inaktive Profile ignorieren Run-Engine und ssh-config-Generator.'}
        </p>
        {action?.profile.protocol === 'ssh' && action.profile.is_active && (
          <p className="text-muted">
            Achtung: Ohne aktives SSH-Profil ist das System im Run-Wizard nicht mehr wählbar.
          </p>
        )}
        {error && <ErrorAlert error={error} />}
      </div>
    </Dialog>
  );
}

function userAtPort(profile: ConnectionProfile): string {
  const user = profile.username && profile.username.trim() !== '' ? profile.username : '–';
  const port = profile.port != null ? String(profile.port) : '–';
  return `${user}@${port}`;
}

function protocolTone(protocol: string): 'primary' | 'success' | 'neutral' {
  switch (protocol) {
    case 'ssh':
      return 'success';
    case 'rdp':
      return 'primary';
    default:
      return 'neutral';
  }
}

// --- Services (IST/SOLL-Drift-Sicht) --------------------------------------

export interface DriftBadgeSpec {
  tone: BadgeTone;
  label: string;
  /** Als title-Attribut gerendert (z.B. Stale-Hinweis). */
  tooltip?: string;
}

/**
 * Badge-Mapping der Drift-Sicht als PURE Funktion (testbar ohne Rendering).
 * Spiegel des Backend-Mappings routes.systems.drift_status_from_assessment:
 *   ok             -> grün  "aktuell"
 *   outdated_minor -> gelb   "Update (minor)"
 *   outdated_major -> rot    "Update (major)"
 *   stale          -> grau   "Stand veraltet" + Tooltip "aelter als 7 Tage"
 *   unknown/fehlt  -> grau   "unbekannt"
 */
export function driftStatusBadge(
  status: ServiceDriftStatus | null | undefined
): DriftBadgeSpec {
  switch (status) {
    case 'ok':
      return { tone: 'success', label: 'aktuell' };
    case 'outdated_minor':
      return { tone: 'warning', label: 'Update (minor)' };
    case 'outdated_major':
      return { tone: 'danger', label: 'Update (major)' };
    case 'stale':
      return { tone: 'neutral', label: 'Stand veraltet', tooltip: 'älter als 7 Tage' };
    case 'unknown':
    default:
      return { tone: 'neutral', label: 'unbekannt' };
  }
}

function DriftStatusBadge({
  status,
}: {
  status: ServiceDriftStatus | null | undefined;
}): JSX.Element {
  const spec = driftStatusBadge(status);
  return (
    // Badge kennt kein title-Prop — Tooltip via Wrapper-Span.
    <span title={spec.tooltip} data-testid="service-drift-status">
      <Badge tone={spec.tone}>{spec.label}</Badge>
    </span>
  );
}

const SERVICE_COLUMNS: ReadonlyArray<Column<ServiceWithDrift>> = [
  {
    id: 'dienst',
    header: 'Dienst',
    filterValue: (service) =>
      [service.service_type, service.instance_name ?? ''].join(' '),
    cell: (service) => (
      <span className="inline-flex flex-wrap items-center gap-1">
        <Badge tone="primary">{service.service_type}</Badge>
        {service.instance_name && (
          <span className="text-xs text-muted">{service.instance_name}</span>
        )}
      </span>
    ),
  },
  {
    id: 'ist',
    header: 'IST',
    className: 'font-mono text-xs',
    filterValue: (service) => service.observed_version ?? '',
    cell: (service) => service.observed_version ?? '–',
  },
  {
    id: 'soll',
    header: 'SOLL',
    className: 'font-mono text-xs',
    filterValue: (service) => service.target_version ?? '',
    cell: (service) => service.target_version ?? '–',
  },
  {
    id: 'status',
    header: 'Status',
    filter: 'select',
    filterValue: (service) => driftStatusBadge(service.drift_status).label,
    cell: (service) => <DriftStatusBadge status={service.drift_status} />,
  },
  {
    id: 'web-urls',
    header: 'Web-URLs',
    filterValue: (service) =>
      service.web_urls && service.web_urls.length > 0 ? service.web_urls.join(' ') : '',
    cell: (service) =>
      service.web_urls && service.web_urls.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {service.web_urls.map((url) => (
            <li key={url}>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                {url}
                <ExternalLink className="size-3" aria-hidden="true" />
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <span className="text-muted">–</span>
      ),
  },
  {
    id: 'stand',
    header: 'Stand',
    className: 'text-muted',
    filterValue: (service) => {
      const observedAt = service.observed_at ?? service.observed_version_at;
      const stamp = observedAt ? formatDateTime(observedAt) : '';
      return service.detection_method ? `${stamp} via ${service.detection_method}`.trim() : stamp;
    },
    cell: (service) => {
      const observedAt = service.observed_at ?? service.observed_version_at;
      return (
        <>
          {formatDateTime(observedAt)}
          {service.detection_method && (
            <span className="ml-1 text-xs">via {service.detection_method}</span>
          )}
        </>
      );
    },
  },
];

function ServicesTab({ systemId }: { systemId: string }): JSX.Element {
  const { data, isLoading, isError, error } = useSystemServices(systemId);
  if (isLoading) return <SkeletonRows rows={3} cols={5} />;
  if (isError) return <ErrorAlert error={error} />;
  if (!data || data.length === 0) {
    return (
      <EmptyState
        title="Keine Services erfasst"
        description="Auf diesem System wurden noch keine Service-Stacks (OPNsense, FreePBX, Nextcloud, ...) angelegt."
      />
    );
  }
  return (
    <DataTable
      caption="Services auf diesem System (IST vs. SOLL aus version_targets)"
      columns={SERVICE_COLUMNS}
      rows={data}
      rowKey={(service) => service.id}
      storageKey="system-services"
    />
  );
}

function HistoryTab({
  systemId,
  customerId,
}: {
  systemId: string;
  customerId: string;
}): JSX.Element {
  const { data, isLoading, isError, error } = useRuns({ customerId, limit: 200 });
  const runs = useMemo(
    () => (data?.data ?? []).filter((run) => run.target_system_ids.includes(systemId)),
    [data, systemId]
  );

  if (isLoading) return <SkeletonRows rows={3} cols={5} />;
  if (isError) return <ErrorAlert error={error} />;
  if (runs.length === 0) {
    return (
      <EmptyState
        title="Keine Wartungs-Runs"
        description="Sobald eine Wartung dieses System einschließt, erscheint der zugehörige Run hier."
        actions={
          <Link to="/runs/new" search={{ customers: customerId, systems: systemId }}>
            <Button leftIcon={<PlayCircle className="size-4" />}>Wartung starten</Button>
          </Link>
        }
      />
    );
  }

  return (
    <DataTable
      caption="Wartungs-History dieses Systems"
      columns={HISTORY_COLUMNS}
      rows={runs}
      rowKey={(run) => run.id}
      storageKey="system-history"
    />
  );
}

const HISTORY_COLUMNS: ReadonlyArray<Column<Run>> = [
  {
    id: 'run',
    header: 'Run',
    filterValue: (run) => run.id,
    cell: (run) => (
      <Link
        to="/runs/$id"
        params={{ id: run.id }}
        className="font-mono text-xs text-primary hover:underline"
      >
        {run.id.slice(0, 8)}
      </Link>
    ),
  },
  {
    id: 'modus',
    header: 'Modus',
    filter: 'select',
    filterValue: (run) => run.mode,
    cell: (run) => (
      <Badge tone={run.mode === 'apply' ? 'warning' : 'neutral'}>{run.mode}</Badge>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    filter: 'select',
    filterValue: (run) => runStatusLabel(run.status),
    cell: (run) => <RunStatusBadge status={run.status} />,
  },
  {
    id: 'gestartet',
    header: 'Gestartet',
    className: 'text-muted',
    filterValue: (run) => formatDateTime(run.started_at ?? run.created_at),
    cell: (run) => formatDateTime(run.started_at ?? run.created_at),
  },
  {
    id: 'dauer',
    header: 'Dauer',
    className: 'text-muted',
    filterValue: (run) => {
      const label = runDurationLabel(run);
      return label === '–' ? '' : label;
    },
    cell: (run) => runDurationLabel(run),
  },
];

// --- Pakete (live aus Zabbix) --------------------------------------------

/** Zeilen-Cap gegen DOM-Explosion bei ~2000+ Paketen pro Host. */
const PACKAGE_ROW_CAP = 500;

const PACKAGE_COLUMNS: ReadonlyArray<Column<ZabbixPackageItem>> = [
  {
    id: 'name',
    header: 'Name',
    className: 'font-mono text-xs',
    cell: (pkg) => pkg.name,
  },
  {
    id: 'version',
    header: 'Version',
    className: 'font-mono text-xs',
    cell: (pkg) => pkg.version,
  },
  {
    id: 'arch',
    header: 'Arch',
    className: 'text-muted',
    filter: 'select',
    filterValue: (pkg) => pkg.arch ?? '',
    cell: (pkg) => pkg.arch ?? '–',
  },
  {
    id: 'manager',
    header: 'Manager',
    className: 'text-muted',
    filter: 'select',
    filterValue: (pkg) => pkg.manager ?? '',
    cell: (pkg) => pkg.manager ?? '–',
  },
];

function PackagesTab({
  systemId,
  active,
}: {
  systemId: string;
  active: boolean;
}): JSX.Element {
  const { data, isLoading, isError, error } = useZabbixPackages(systemId, {
    enabled: active,
  });
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const packages = data?.packages ?? [];
    const needle = search.trim().toLowerCase();
    if (!needle) return packages;
    return packages.filter(
      (p) =>
        p.name.toLowerCase().includes(needle) ||
        p.version.toLowerCase().includes(needle) ||
        (p.arch ?? '').toLowerCase().includes(needle) ||
        (p.manager ?? '').toLowerCase().includes(needle)
    );
  }, [data, search]);

  if (isLoading) return <SkeletonRows rows={4} cols={4} />;

  if (isError) {
    if (error instanceof ApiError && error.status === 503) {
      return (
        <EmptyState
          icon={<Package className="size-8" />}
          title="Zabbix ist nicht konfiguriert"
          description="ZABBIX_URL und ZABBIX_API_TOKEN in ops/.env setzen und das Backend neu starten, um Paketdaten live abzurufen."
        />
      );
    }
    return <ErrorAlert error={error} />;
  }
  if (!data) return <ErrorAlert error={new Error('Keine Daten erhalten')} />;

  if (!data.matched_host) {
    return (
      <EmptyState
        icon={<Package className="size-8" />}
        title="System nicht in Zabbix gefunden"
        description={zabbixNoMatchDescription(data)}
      />
    );
  }

  const total = data.packages.length;
  const visible = filtered.slice(0, PACKAGE_ROW_CAP);

  return (
    <div className="flex flex-col gap-4">
      {/* APT-Update-Sicht (itkfm.*-Items) - null = Item fehlt/hat nie
          geliefert (Bestands-Host ohne Agent-Neukonfig): nichts anzeigen. */}
      {(data.pending_updates != null ||
        data.security_updates != null ||
        data.reboot_required != null) && (
        <div className="flex flex-wrap items-center gap-2">
          {data.pending_updates != null && (
            <Badge tone={data.pending_updates > 0 ? 'warning' : 'success'}>
              {data.pending_updates > 0
                ? `${formatNumber(data.pending_updates)} Updates verfügbar`
                : '0 Updates'}
            </Badge>
          )}
          {data.security_updates != null && (
            <Badge tone={data.security_updates > 0 ? 'danger' : 'neutral'}>
              {formatNumber(data.security_updates)} Security
            </Badge>
          )}
          {data.reboot_required === true && (
            <Badge tone="danger">Neustart erforderlich</Badge>
          )}
          {data.updates_checked_at != null && (
            <span className="text-xs text-muted">
              Stand: <RelativeTime value={data.updates_checked_at} />
            </span>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <span>
          <span className="text-muted">OS: </span>
          {data.os_name ?? '–'}
        </span>
        <span className="font-mono text-xs">
          <span className="font-sans text-sm text-muted">Kernel: </span>
          {data.kernel ?? '–'}
        </span>
        <span className="text-muted">
          Stand: <RelativeTime value={data.packages_updated_at} />
        </span>
        <Badge tone="primary">live aus Zabbix</Badge>
        <span className="text-xs text-muted">
          Zabbix-Host: <span className="font-mono">{data.matched_host.host}</span>
        </span>
      </div>

      {total === 0 ? (
        <EmptyState
          icon={<Package className="size-8" />}
          title={data.error ? 'Paketdaten nicht lesbar' : 'Keine Paketdaten'}
          description={
            data.error ??
            'Zabbix liefert für diesen Host keine Paketliste (Item system.sw.packages.get im Template prüfen).'
          }
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="w-full max-w-sm">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Pakete filtern (Name, Version, Arch, Manager)"
                aria-label="Pakete filtern"
                leftIcon={<Search className="size-4" />}
              />
            </div>
            <p className="text-sm text-muted" aria-live="polite">
              gefiltert: {formatNumber(filtered.length)} von {formatNumber(total)}
              {filtered.length > PACKAGE_ROW_CAP &&
                ` – nur die ersten ${formatNumber(PACKAGE_ROW_CAP)} Zeilen werden angezeigt`}
            </p>
          </div>

          {filtered.length === 0 ? (
            <EmptyState
              title="Kein Paket passt auf den Filter"
              description="Suchbegriff anpassen oder leeren."
            />
          ) : (
            <DataTable
              caption="Installierte Pakete (live aus Zabbix)"
              columns={PACKAGE_COLUMNS}
              rows={visible}
              rowKey={(pkg) => `${pkg.name}:${pkg.arch ?? ''}:${pkg.version}`}
              storageKey="system-pakete"
              defaultPageSize={25}
            />
          )}
        </>
      )}
    </div>
  );
}

function zabbixNoMatchDescription(data: ZabbixPackagesResponse): string {
  if (data.reason === 'ambiguous') {
    const list = data.candidates.join(', ');
    return `Mehrere Zabbix-Hosts kommen infrage (${list}). Hostnamen in Zabbix oder im Inventar eindeutig machen.`;
  }
  return 'Kein Zabbix-Host passt auf Hostname, FQDN oder IP-Adressen dieses Systems. Hostnamen abgleichen oder das System in Zabbix aufnehmen.';
}

// --- Helper -------------------------------------------------------------

/** Bitwarden-Item-ID auf das erste UUID-Segment kürzen (voll im title-Attribut). */
function shortBwItemId(id: string): string {
  return id.length > 13 ? `${id.slice(0, 8)}…` : id;
}

function runDurationLabel(run: Run): string {
  if (run.duration_seconds != null) {
    const sec = run.duration_seconds;
    if (sec < 60) return `${sec}s`;
    if (sec < 3600) return `${Math.round(sec / 60)}m`;
    return `${(sec / 3600).toFixed(1)}h`;
  }
  if (run.status === 'running' || run.status === 'pending') return 'läuft …';
  return '–';
}

function scopeTone(s: SystemScope | null | undefined): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (s) {
    case 'in_scope':
      return 'success';
    case 'manual_only':
    case 'external':
      return 'warning';
    case 'excluded':
    case 'eol':
    case 'offline':
      return 'danger';
    default:
      return 'neutral';
  }
}

function updatePolicyTone(
  p: string | null | undefined
): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (p) {
    case 'auto':
      return 'success';
    case 'manual':
    case 'coordinated':
      return 'warning';
    case 'never':
      return 'danger';
    default:
      return 'neutral';
  }
}
